import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Tag, Badge } from '@/components/Tag';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/Button';
import { Alert, Empty, ErrorState } from '@/components/States';
import { orderStatus } from '@/lib/status';
import { dateTime, num, qty } from '@/lib/format';
import { fileSize, fullTime } from './format';
import { TypeMark, titleOf, KitNote, targetOf } from '../exceptions/ExceptionBits';
import { isOpen, resolution, responsibility, statusOf, typeOf } from '../exceptions/status';
import type { ExceptionBase } from '../exceptions/types';
import { groupShipmentLines, orderProgress, shipmentTotals } from './bundle';
import { useShipmentDetail } from './api';
import { shipmentStatusOf } from './status';
import { ExceptionCreateFlow } from '../exceptions/ExceptionCreateFlow';
import { usePagedList } from '../inventory/api';
import { signedQuantity, transactionKind } from '../inventory/status';
import type { InventoryTransaction } from '../inventory/types';
import { Cols, DetailFrame, KV, Kpis, LegacyButton, Loading, Panel, type Kpi } from './DetailParts';
import { FlatLines, ShipmentLines } from './ShipmentLines';
import { ShipmentPrintDialog } from './ShipmentPrintDialog';
import { ShipmentLogisticsDialog } from './ShipmentLogisticsDialog';
import type { ShipmentListItem, ShipmentOrder } from './types';
import s from './ShipmentDetail.module.css';

function CopyText({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  if (!text) return <span className="em">—</span>;
  return (
    <span className={s.copy}>
      <span className="tnum">{text}</span>
      <button
        type="button"
        aria-label="复制物流单号"
        title="复制"
        onClick={() => {
          navigator.clipboard?.writeText(text).then(() => {
            setDone(true);
            window.setTimeout(() => setDone(false), 1500);
          }, () => undefined);
        }}
      >
        <Icon name={done ? 'check' : 'copy'} size={14} />
      </button>
      {done && <small className={s.copied}>已复制</small>}
    </span>
  );
}

function WideMoreMenu({ path }: { path: string }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false); };
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => { document.removeEventListener('pointerdown', closeOutside); document.removeEventListener('keydown', closeOnEscape); };
  }, [open]);
  return <div className={s.wideMoreRoot} ref={root}>
    <button type="button" aria-label="更多操作" aria-expanded={open} onClick={() => setOpen((value) => !value)}><Icon name="more" size={18} /></button>
    {open && <div className={s.wideMorePopup} role="menu" aria-label="批次操作">
      <LegacyButton path={path}>在老版中操作</LegacyButton>
    </div>}
  </div>;
}

/** 关联异常一行：类型色点 + 标题 + 唯一实底标签（状态）；套装异常带套装名 */
function ExceptionRow({ e, orderId, canOpen, canClose, canResolve }: { e: ExceptionBase; orderId: string; canOpen: boolean; canClose: boolean; canResolve: boolean }) {
  const st = statusOf(e.status);
  const t = targetOf(e);
  const actionButtons = isOpen(e) && (canClose || canResolve) ? (
    <div className={s.excActions}>
      {canClose && <Link to={`/exceptions/${e.id}?order=${orderId}&action=close`} className={s.excAction}>关闭异常</Link>}
      {canResolve && <Link to={`/exceptions/${e.id}?order=${orderId}&action=resolve`} className={`${s.excAction} ${s.excActionPrimary}`}>去处理</Link>}
    </div>
  ) : null;
  const meta = [
    `${e.createdBy?.name || '—'} ${dateTime(e.createdAt)} 登记`,
    e.responsibility ? `责任 ${responsibility[e.responsibility] || e.responsibility}` : '责任方待判定',
    e.resolutionAction ? resolution[e.resolutionAction] || e.resolutionAction : null,
  ].filter(Boolean).join(' · ');
  const body = (
    <>
      <div className={s.et}>
        <TypeMark type={e.type} />
        <b>{titleOf(e)}</b>
        <Tag tone={st.tone} sm>{st.label}</Tag>
      </div>
      {t.bundle && <div className={s.ek}><KitNote name={t.bundle} code={t.bundleCode} /> 的组件</div>}
      <p>{meta}</p>
      {e.note && <q className={s.quote}>{e.note}</q>}
    </>
  );
  return (
    <li>
      {canOpen ? (
        <Link to={`/exceptions/${e.id}?order=${orderId}`} className={s.erow}>{body}<Icon name="chev" className={s.chev} /></Link>
      ) : (
        <div className={s.erow}>{body}</div>
      )}
      {actionButtons}
    </li>
  );
}

function Progress({ order, thisUnits, reship }: { order: ShipmentOrder; thisUnits: number; reship: boolean }) {
  const p = orderProgress(order);
  const st = orderStatus[order.status];
  const mine = Math.min(thisUnits, p.shipped);
  const others = Math.max(p.shipped - mine, 0);
  const pct = (n: number) => (p.ordered > 0 ? `${Math.min(100, (n / p.ordered) * 100)}%` : '0%');
  return (
    <div className={s.prog}>
      <div className={s.ph}>
        <Link to={`/orders/${order.id}`} className="tnum">{order.orderNo}</Link>
        {st && <Tag tone={st.tone} sm>{st.label}</Tag>}
        <span className={s.pt}>累计已发 <b className="tnum">{qty(p.shipped)}</b> / {qty(p.ordered)}</span>
      </div>
      <div className={s.bar} role="img" aria-label={`其他批次 ${qty(others)}，本批 ${qty(mine)}，剩余 ${qty(p.remaining)}`}>
        <i style={{ width: pct(others) }} />
        <i className={s.b} style={{ width: pct(mine) }} />
      </div>
      <div className={s.lg}>
        <span><i />其他批次 <b className="tnum">{qty(others)}</b></span>
        <span><i className={s.b} />本批 <b className="tnum">{qty(mine)}</b></span>
        <span><i className={s.n} />剩余 <b className="tnum">{qty(p.remaining)}</b></span>
      </div>
      <p className={s.hint}>
        按订单行计数，套装 1 套记 1、单品 1 个记 1；破损/补耗不计入已发。
        {reship && ' 本批是异常补发，不改订单已发数量。'}
      </p>
    </div>
  );
}

function ShipmentStockPanel({ shipmentId, shipmentNo }: { shipmentId: string; shipmentNo: string }) {
  const list = usePagedList<InventoryTransaction>('/api/v2/inventory/transactions',
    { keyword: shipmentNo, all: '1' }, { pageSize: 200, append: true });
  const rows = list.items.filter((transaction) =>
    transaction.referenceType === 'Shipment' && transaction.referenceId === shipmentId &&
    (transaction.type === 'SALE_OUT' || transaction.type === 'ADJUST' && transaction.direction === 'OUT'),
  );
  return <Panel title="库存流水" extra={list.loading && !list.items.length ? '读取中…' : list.hasNext ? `已加载 ${list.items.length} / ${list.total} 条匹配流水` : `${rows.length} 条本批出库`} flush>
    {list.error && <p className={s.stockMessage} role="alert">库存流水读取失败：{list.error.message} <button type="button" onClick={list.reload}>重试</button>。当前记录可能不完整。</p>}
    {list.loading && !list.items.length ? <p className={s.stockMessage}>正在读取本批出库流水…</p> : rows.length ? <div className={s.stockScroll}><table className={s.stockTable}>
      <thead><tr><th>时间</th><th>类型</th><th>产品</th><th>数量</th><th>变动后可用</th></tr></thead>
      <tbody>{rows.map((transaction) => {
        const amount = signedQuantity(transaction);
        return <tr key={transaction.id}><td className="tnum">{dateTime(transaction.createdAt)}</td><td><Tag tone={transactionKind(transaction).tone} sm>{transactionKind(transaction).label}</Tag></td><td>{transaction.product.name}</td><td className={s.stockOut}>{amount.sign}{qty(Math.abs(amount.value))}</td><td className="tnum">{transaction.availableAfter === null || transaction.availableAfter === undefined ? '—' : qty(transaction.availableAfter)}</td></tr>;
      })}</tbody>
    </table></div> : !list.hasNext && !list.error && <p className={s.stockMessage}>这批没有直接关联的出库流水。</p>}
    {list.hasNext && <button type="button" className={s.stockMore} disabled={list.loading} onClick={list.loadMore}>{list.loading ? '加载中…' : '继续加载匹配流水'}</button>}
    <p className={s.stockHint}>仅显示直接关联此批次的出库和报损记录；历史余额无法可靠回算时显示“—”。</p>
  </Panel>;
}

export function ShipmentDetail({ no, seed, embedded }: { no: string; seed?: ShipmentListItem; embedded?: boolean }) {
  const ff = useFormFactor();
  const can = useSession((st) => st.can);
  const [params] = useSearchParams();
  const [printOpen, setPrintOpen] = useState(() => params.get('print') === '1');
  const [logisticsOpen, setLogisticsOpen] = useState(false);
  const [logisticsAnchor, setLogisticsAnchor] = useState<HTMLButtonElement | null>(null);
  const [createExceptionOpen, setCreateExceptionOpen] = useState(false);
  const [phoneMenuOpen, setPhoneMenuOpen] = useState(false);
  const { item, order, shipment, orderError, loading, error, reload } = useShipmentDetail(no, seed);

  // 出错 / 加载中也保留页头：手机深层页没有 Tab 栏，返回键是唯一出口
  if (error || !item) {
    return (
      <DetailFrame embedded={embedded} fallback="/shipments" title={no}>
        {error ? <ErrorState title="发货批次加载失败" message={error.message} detail={`GET /api/shipments?keyword=${no}`} onRetry={reload} /> : <Loading />}
      </DetailFrame>
    );
  }

  const st = shipmentStatusOf(item.status);
  const groups = shipment ? groupShipmentLines(shipment, order?.lines) : null;
  const totals = groups ? shipmentTotals(groups) : null;
  const flatShip = item.lines.reduce((a, l) => a + num(l.quantity), 0);
  const flatLoss = item.lines.reduce((a, l) => a + num(l.lossQuantity), 0);
  const related = (order?.exceptions || []).filter((e) => e.shipmentId === item.id);
  const reshipFrom = (order?.exceptions || []).filter((e) => e.reshipmentShipmentId === item.id);
  const noteForLine = (line: { orderLineId?: string; productId: string }) => related
    .filter((e) => e.orderLineId && line.orderLineId ? e.orderLineId === line.orderLineId : e.productId === line.productId)
    .map((e) => {
      const note = e.note?.trim();
      if (!note) return note;
      const reason = note.split(/[：:]/, 2)[1]?.trim() || note;
      return reason.split(/[，,]/, 1)[0].trim();
    })
    .filter((note): note is string => !!note)
    .filter((note, index, all) => all.indexOf(note) === index)
    .join('；');
  const openCount = order ? related.filter(isOpen).length : item.exceptions.filter(isOpen).length;
  const canEx = can('exceptions.read');
  const wide = !embedded && ff !== 'phone';

  const kpis: Kpi[] = ff !== 'desktop' ? [
    { label: '套装', value: totals ? qty(totals.sets) : '—', unit: '套' },
    { label: '单品', value: totals ? qty(totals.singles) : qty(flatShip), unit: '个' },
    { label: '破损 / 补耗', value: qty(flatLoss), unit: '个', tone: flatLoss ? 'warn' : undefined },
    { label: '实际出库', value: qty(flatShip + flatLoss), unit: '个', tone: 'hl' },
  ] : [
    { label: '产品行', value: qty(item.lines.length) },
    { label: '发货数量', value: qty(flatShip), unit: '个' },
    { label: '破损 / 补耗', value: qty(flatLoss), unit: '个', tone: flatLoss ? 'warn' : undefined },
    { label: '实际出库', value: qty(flatShip + flatLoss), unit: '个' },
  ];

  const main = (
    <>
      {orderError && (
        <Alert tone="warn" title="没取到订单明细，套装无法按套合并">
          下面按产品逐行列出（{orderError.message}）。发货数量、破损/补耗不受影响。
        </Alert>
      )}
      {reshipFrom.map((e) => (
        <Alert key={e.id} tone="info" icon="return" title={`这是异常补发批次：${titleOf(e)}`}>
          由 {typeOf(e.type).label}异常生成（{statusOf(e.status).label}），补发出库不改订单的已发数量。
        </Alert>
      ))}
      <Kpis items={kpis} />
      <Panel title="发货明细" extra="实际出库 = 发货数量 + 破损/补耗，两者都已写库存流水 · 套装按套发货，破损/补耗记在组件上" flush>
        {loading && !groups ? <Loading /> : groups ? <ShipmentLines groups={groups} noteForLine={noteForLine} /> : <FlatLines lines={item.lines} noteForLine={noteForLine} />}
        {item.note && <p className={s.note}><span>备注</span>{item.note}</p>}
      </Panel>
      {canEx && (
        <Panel
          title={<>关联异常{openCount > 0 ? <Badge>{openCount}</Badge> : related.length > 0 ? <Badge tone="grey">{related.length}</Badge> : null}</>}
          extra={can('exceptions.create') && can('orders.read.all', 'orders.read.own', 'orders.read.assigned') ? <button type="button" className={s.link} onClick={() => setCreateExceptionOpen(true)}>登记异常</button> : undefined}
          flush
        >
          {order ? (
            related.length ? (
              <ul className={s.exc}>{related.map((e) => <ExceptionRow key={e.id} e={e} orderId={item.orderId} canOpen={canEx} canClose={can('exceptions.close')} canResolve={can('exceptions.resolve')} />)}</ul>
            ) : (
              <p className={s.none}>这一批没有登记异常。</p>
            )
          ) : item.exceptions.length ? (
            <ul className={s.exc}>
              {item.exceptions.map((e) => (
                <li key={e.id}>
                  <Link to={`/exceptions/${e.id}?order=${item.orderId}`} className={s.erow}>
                    <div className={s.et}><TypeMark type={e.type} /><b>{typeOf(e.type).label}异常</b><Tag tone={statusOf(e.status).tone} sm>{statusOf(e.status).label}</Tag></div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className={s.none}>{loading ? '加载中…' : '这一批没有登记异常。'}</p>
          )}
        </Panel>
      )}
      {can('inventory.transactions.read') && <ShipmentStockPanel shipmentId={item.id} shipmentNo={item.shipmentNo} />}
    </>
  );

  const files = shipment?.files || [];
  const openLogistics = (event: MouseEvent<HTMLButtonElement>) => {
    setLogisticsAnchor(event.currentTarget);
    setLogisticsOpen(true);
  };
  const closeLogistics = () => {
    setLogisticsOpen(false);
    setLogisticsAnchor(null);
  };
  const side = (
    <>
      <Panel title="物流信息" extra={can('shipments.logistics.write') ? <button type="button" className={s.link} onClick={openLogistics}>修改</button> : undefined}>
        <KV
          rows={[
            ['物流公司', item.logisticsCompany || '—'],
            ['物流单号', <CopyText key="t" text={item.trackingNo} />],
            ['发货时间', <span key="d" className="tnum">{fullTime(item.shippedAt)}</span>],
            ['出库人', item.createdBy?.name || '—'],
            ['出货仓库', item.warehouse ? `${item.warehouse.name}` : '—'],
          ]}
        />
      </Panel>
      {order && (order.recipientName || order.recipientAddress) && (
        <Panel title="收货信息">
          <KV
            rows={[
              ['收货人', <>{order.recipientName || '—'} {order.recipientPhone && <a href={`tel:${order.recipientPhone}`} className="tnum em">{order.recipientPhone}</a>}</>],
              ['地址', order.recipientAddress || '—'],
            ]}
          />
        </Panel>
      )}
      {order && (
        <Panel title="订单进度">
          <Progress order={order} thisUnits={totals && !reshipFrom.length ? totals.sets + totals.singles : 0} reship={reshipFrom.length > 0} />
        </Panel>
      )}
      {shipment && (
        <Panel title="附件" flush>
          {files.length ? (
            <ul className={s.files}>
              {files.map((f) => {
                const image = /\.(png|jpe?g|webp|gif|heic)$/i.test(f.attachment.fileName) || f.attachment.fileType?.startsWith('image');
                const ext = f.attachment.fileName.split('.').pop()?.toUpperCase() || '文件';
                const label = image ? '图片' : ext === 'PDF' ? 'PDF' : ext === 'XLS' || ext === 'XLSX' ? '表格' : ext;
                const tone = image ? 'blue' : ext === 'PDF' ? 'red' : 'none';
                return (
                  <li key={f.id}>
                    <a className={s.fileCard} href={f.attachment.fileUrl} target="_blank" rel="noreferrer">
                      <span className={s.fileThumb}><Icon name={image ? 'img' : 'file'} size={20} /></span>
                      <b title={f.attachment.fileName}>{f.attachment.fileName}</b>
                      <span className={s.fileMeta}><Tag tone={tone} sm>{label}</Tag>{fileSize(f.attachment.fileSize) && <small>{fileSize(f.attachment.fileSize)}</small>}</span>
                      {f.attachment.usageNote && <small className={s.fileNote}>{f.attachment.usageNote}</small>}
                    </a>
                  </li>
                );
              })}
            </ul>
          ) : <p className={s.none}>本批次暂无附件。</p>}
        </Panel>
      )}
    </>
  );

  const canCreateException = can('exceptions.create') && can('orders.read.all', 'orders.read.own', 'orders.read.assigned');
  const phoneHeaderActions = <div className={s.phoneMoreRoot}>
    <button type="button" className={s.phoneMore} aria-label="更多操作" aria-expanded={phoneMenuOpen} onClick={() => setPhoneMenuOpen((open) => !open)}><Icon name="more" size={20} /></button>
    {phoneMenuOpen && <div className={s.phoneMenu} role="menu">
      {can('shipments.logistics.write') && <button type="button" role="menuitem" onClick={(event) => { setPhoneMenuOpen(false); openLogistics(event); }}>修改物流信息</button>}
      {canCreateException && <button type="button" role="menuitem" onClick={() => { setPhoneMenuOpen(false); setCreateExceptionOpen(true); }}>登记异常</button>}
    </div>}
  </div>;
  const phoneFooterActions = <>
    {canCreateException && <Button onClick={() => setCreateExceptionOpen(true)}>登记异常</Button>}
    <Button variant="primary" icon="down" disabled={loading || !item.lines.length} disabledReason={loading ? '发货明细加载中' : '这一批没有明细行'} onClick={() => setPrintOpen(true)}>打印发货单</Button>
  </>;
  const printAction = <Button variant="primary" icon="print" disabled={loading || !item.lines.length} disabledReason={loading ? '发货明细加载中' : '这一批没有明细行'} onClick={() => setPrintOpen(true)}>打印发货单</Button>;
  const logisticsAction = can('shipments.logistics.write') && <Button onClick={openLogistics}>修改物流信息</Button>;
  const exceptionAction = canCreateException && <Button onClick={() => setCreateExceptionOpen(true)}>登记异常</Button>;

  return <>
    <DetailFrame
      embedded={embedded}
      compactSub={!embedded && ff === 'desktop'}
      fallback="/shipments"
      title={item.shipmentNo}
      phoneTitle="发货批次"
      phoneActions={phoneHeaderActions}
      tags={<><Tag tone={st.tone}>{st.label}</Tag>{openCount > 0 && <Badge>{openCount} 个异常</Badge>}</>}
      actions={
        ff === 'phone' ? phoneFooterActions : ff === 'tablet' ? (
          <>
            {exceptionAction}
            {logisticsAction}
            {printAction}
            <WideMoreMenu path={`/orders/${item.orderId}?tab=shipping`} />
          </>
        ) : (
          <>
            {printAction}
            {logisticsAction}
            {exceptionAction}
            <WideMoreMenu path={`/orders/${item.orderId}?tab=shipping`} />
          </>
        )
      }
      sub={ff === 'phone' ? (
        <>
          <span className={s.phoneBatchLine}><b className="tnum">{item.shipmentNo}</b><Tag tone={st.tone} sm>{st.label}</Tag>{openCount > 0 && <Badge>{openCount} 个异常</Badge>}</span>
          <span className={s.phoneSubLine}><Link to={`/orders/${item.orderId}`} className={`${s.olink} tnum`}>{item.order.orderNo}</Link> · {item.order.customer.name} · {item.warehouse?.name || '—'}</span>
          <span className={s.phoneSubLine}>{item.createdBy?.name || '—'}出库 · <span className="tnum">{fullTime(item.shippedAt)}</span></span>
        </>
      ) : (
        <>
          <Link to={`/orders/${item.orderId}`} className={`${s.olink} tnum`}>{item.order.orderNo}</Link>
          {' · '}{item.order.customer.name} · {item.warehouse?.name || '—'} · <span className={s.nw}>{item.createdBy?.name || '—'}出库 <span className="tnum">{fullTime(item.shippedAt)}</span></span>
        </>
      )}
    >
      {!item.lines.length ? <Empty title="这一批没有明细行">可能是数据异常，请在老版核对。</Empty> : <Cols wide={wide} main={main} side={side} />}
    </DetailFrame>
    {printOpen && <ShipmentPrintDialog item={item} order={order} groups={groups} onClose={() => setPrintOpen(false)} />}
    {logisticsOpen && <ShipmentLogisticsDialog shipment={item} anchorElement={logisticsAnchor} onClose={closeLogistics} onSuccess={reload} />}
    {createExceptionOpen && <ExceptionCreateFlow initialOrderId={item.orderId} initialShipmentId={item.id} onClose={() => setCreateExceptionOpen(false)} onSuccess={reload} />}
  </>;
}
